import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  DEFAULT_RETAIL_VALUE_SOURCE, ExistingExposure, discountedVehicleValue, isVehicleCollateral,
} from '../../../constants/collateralValue';
import { useRecognisedPercent } from '../../../services/collateralSettings';

type CollateralItem = {
  assetDescription: string;
  make?: string;
  yearModel?: string;
  registrationNumber?: string;
  otherDetails?: string;
  ownerName: string;
  ownerId: string;
  value: number;
  /** A vehicle's odometer reading (finding #11) */
  mileageKm?: number;
  /** A vehicle's retail value and where it came from; value is then the retail value less the discount (finding #10) */
  retailValue?: number;
  retailValueSource?: string;
  /** A vehicle's insurance; the credit manual requires it to be insured for the facility (finding #10) */
  insured?: boolean;
  insurer?: string;
  insurancePolicyNumber?: string;
}

type CollateralDirector = { firstName?: string; lastName?: string; idNumber?: string };

type CollateralForm = {
  collaterals: CollateralItem[];
  company?: { directors?: CollateralDirector[]; companyName?: string; companyRegNumber?: string };
  loan?: { loanAmount?: number };
}

const formatRand = (value: number) =>
  value.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });

// Default empty collateral with required fields initialized
const emptyCollateral: CollateralItem = {
  assetDescription: '',
  ownerName: '',
  ownerId: '',
  value: 0
};

/**
 * existing: what a returning customer already owes Tirhelo on open loan accounts. The security is measured
 * against Tirhelo's exposure after this loan, as the credit manual (7.1) does (finding #10).
 */
export default function CollateralStep({ existing }: { existing?: ExistingExposure } = {}) {
  const { register, formState: { errors }, watch, setValue, clearErrors, getValues } = useFormContext<CollateralForm>();
  const [hasCollateral, setHasCollateral] = useState(true);
  const [collaterals, setCollaterals] = useState<Array<{id: string}>>([{ id: '1' }]);
  // Who owns each collateral: a director ('d0', 'd1'...), the company, or a third party (finding #8)
  const [ownerChoice, setOwnerChoice] = useState<Record<number, string>>({});

  const directors = (watch('company.directors') || []).filter(d => d && (d.firstName || d.lastName));
  const loanAmount = Number(watch('loan.loanAmount')) || 0;
  // The recognised share of a vehicle's retail value, from Settings > Loan (finding #29)
  const recognisedPercent = useRecognisedPercent();
  const watchedCollaterals = watch('collaterals') || [];
  const lentAgainst = watchedCollaterals.reduce((sum, c) => sum + (Number(c?.value) || 0), 0);
  const alreadyOwed = existing?.total || 0;
  const exposureAfter = loanAmount + alreadyOwed;

  const companyName = watch('company.companyName') || '';
  const companyRegNumber = watch('company.companyRegNumber') || '';

  // The choice made here, or, for collateral captured before, worked out from the owner's details
  const ownerOf = (index: number): string => {
    if (ownerChoice[index] !== undefined) return ownerChoice[index];
    const c = watchedCollaterals[index];
    const id = (c?.ownerId || '').trim();
    if (!id) return '';
    const d = directors.findIndex(x => (x.idNumber || '').trim() === id);
    if (d >= 0) return `d${d}`;
    if (companyRegNumber && id === companyRegNumber.trim()) return 'company';
    return 'third';
  };

  const chooseOwner = (index: number, key: string) => {
    setOwnerChoice(prev => ({ ...prev, [index]: key }));
    const fill = (name: string, id: string) => {
      setValue(`collaterals.${index}.ownerName`, name, { shouldValidate: true });
      setValue(`collaterals.${index}.ownerId`, id, { shouldValidate: true });
    };
    if (key === 'company') {
      fill(companyName, companyRegNumber);
    } else if (key === 'third') {
      fill('', '');
    } else if (key.startsWith('d')) {
      const director = directors[Number(key.slice(1))];
      if (director) fill(`${director.firstName || ''} ${director.lastName || ''}`.trim(), director.idNumber || '');
    }
  };

  // The value lent against follows the retail value: retail less the discount (finding #10)
  const onRetailValue = (index: number, raw: string) => {
    const retail = raw === '' ? undefined : Number(raw);
    const discounted = discountedVehicleValue(retail, recognisedPercent);
    if (discounted !== undefined) {
      setValue(`collaterals.${index}.value`, discounted, { shouldValidate: true });
    }
    if (retail && !getValues(`collaterals.${index}.retailValueSource`)) {
      setValue(`collaterals.${index}.retailValueSource`, DEFAULT_RETAIL_VALUE_SOURCE);
    }
  };

  // Initialize collaterals array if it doesn't exist
  useEffect(() => {
    const currentCollaterals = getValues('collaterals');
    if (!currentCollaterals || currentCollaterals.length === 0) {
      setValue('collaterals', [{ ...emptyCollateral }]);
    }
  }, [setValue, getValues]);

  // Clear validation errors and reset form values when user toggles collateral
  useEffect(() => {
    if (!hasCollateral) {
      // Clear all collateral validation errors
      clearErrors('collaterals');

      // Reset collaterals to empty array
      setValue('collaterals', []);
      setCollaterals([]);
    } else if (collaterals.length === 0) {
      // Add one empty collateral if user enables collateral
      setCollaterals([{ id: '1' }]);
      setValue('collaterals', [{ ...emptyCollateral }]);
    }
  }, [hasCollateral, clearErrors, setValue, collaterals.length]);

  const addCollateral = () => {
    const newId = String(Date.now());
    setCollaterals([...collaterals, { id: newId }]);

    // Get current collaterals and add a new empty one
    const currentCollaterals = getValues('collaterals') || [];
    setValue('collaterals', [...currentCollaterals, { ...emptyCollateral }]);
  };

  const removeCollateral = (index: number) => {
    const updatedCollaterals = [...collaterals];
    updatedCollaterals.splice(index, 1);
    setCollaterals(updatedCollaterals);

    // Get current collaterals and remove the one at index
    const currentCollaterals = getValues('collaterals') || [];
    const updatedFormCollaterals = [...currentCollaterals];
    updatedFormCollaterals.splice(index, 1);
    setValue('collaterals', updatedFormCollaterals);
  };

  return (
    <div className="form-section">
      <h6 className="form-section-title">Collateral Information</h6>
      <p className="text-muted mb-4">
        Providing collateral may improve your chances of loan approval and potentially secure better interest rates.
      </p>

      <div className="form-check form-switch mb-4">
        <input
          className="form-check-input"
          type="checkbox"
          id="hasCollateral"
          checked={hasCollateral}
          onChange={(e) => setHasCollateral(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="hasCollateral">
          I want to provide collateral for this loan
        </label>
      </div>

      {hasCollateral ? (
        <>
          {collaterals.map((collateral, index) => (
            <div key={collateral.id} className="card mb-4 p-3 border">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Collateral #{index + 1}</h6>
                {collaterals.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeCollateral(index)}
                  >
                    <i className="fa fa-trash me-1"></i>
                    Remove
                  </button>
                )}
              </div>

              <div className="row g-3">
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`assetDescription-${index}`}>Asset Description</label>
                    <textarea
                      className={`form-control ${errors.collaterals?.[index]?.assetDescription ? 'is-invalid' : ''}`}
                      id={`assetDescription-${index}`}
                      rows={3}
                      placeholder="Describe the asset you're providing as collateral"
                      {...register(`collaterals.${index}.assetDescription`, {
                        required: hasCollateral ? 'Asset description is required' : false
                      })}
                    ></textarea>
                    {errors.collaterals?.[index]?.assetDescription && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.assetDescription?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label htmlFor={`make-${index}`}>Make/Brand (if applicable)</label>
                    <input
                      type="text"
                      className={`form-control ${errors.collaterals?.[index]?.make ? 'is-invalid' : ''}`}
                      id={`make-${index}`}
                      placeholder="Enter make or brand"
                      {...register(`collaterals.${index}.make`)}
                    />
                    {errors.collaterals?.[index]?.make && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.make?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label htmlFor={`yearModel-${index}`}>Year/Model (if applicable)</label>
                    <input
                      type="text"
                      className={`form-control ${errors.collaterals?.[index]?.yearModel ? 'is-invalid' : ''}`}
                      id={`yearModel-${index}`}
                      placeholder="Enter year and model"
                      {...register(`collaterals.${index}.yearModel`)}
                    />
                    {errors.collaterals?.[index]?.yearModel && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.yearModel?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`registrationNumber-${index}`}>Registration Number (if applicable)</label>
                    <input
                      type="text"
                      className={`form-control ${errors.collaterals?.[index]?.registrationNumber ? 'is-invalid' : ''}`}
                      id={`registrationNumber-${index}`}
                      placeholder="Enter registration number"
                      {...register(`collaterals.${index}.registrationNumber`)}
                    />
                    {errors.collaterals?.[index]?.registrationNumber && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.registrationNumber?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-group">
                    <label htmlFor={`mileageKm-${index}`}>Mileage (km, if a vehicle)</label>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      id={`mileageKm-${index}`}
                      placeholder="e.g. 85000"
                      {...register(`collaterals.${index}.mileageKm`, { setValueAs: v => (v === '' || v === null ? undefined : Number(v)) })}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-group">
                    <label htmlFor={`retailValue-${index}`}>Retail Value (R, if a vehicle)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      className="form-control"
                      id={`retailValue-${index}`}
                      placeholder="e.g. WeBuyCars retail price"
                      {...register(`collaterals.${index}.retailValue`, {
                        setValueAs: v => (v === '' || v === null ? undefined : Number(v)),
                        onChange: e => onRetailValue(index, e.target.value),
                      })}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-group">
                    <label htmlFor={`retailValueSource-${index}`}>Retail Value Source</label>
                    <input
                      type="text"
                      maxLength={60}
                      className="form-control"
                      id={`retailValueSource-${index}`}
                      placeholder={DEFAULT_RETAIL_VALUE_SOURCE}
                      {...register(`collaterals.${index}.retailValueSource`)}
                    />
                  </div>
                </div>

                {discountedVehicleValue(watchedCollaterals[index]?.retailValue, recognisedPercent) !== undefined && (
                  <div className="col-12">
                    <div className="alert alert-light border small mb-0">
                      <i className="fa fa-calculator me-2 text-primary"></i>
                      Recognised value: <strong>{formatRand(discountedVehicleValue(watchedCollaterals[index]?.retailValue, recognisedPercent) as number)}</strong>
                      {' '}({recognisedPercent}% of the retail value, from Settings). The loan qualifies up to this amount, and it is
                      filled in below as the value lent against.
                    </div>
                  </div>
                )}

                {isVehicleCollateral(watchedCollaterals[index]) && (
                  <>
                    <div className="col-12 col-md-4">
                      <div className="form-group">
                        <label htmlFor={`insured-${index}`}>Is the vehicle insured?</label>
                        <select
                          className="form-select"
                          id={`insured-${index}`}
                          {...register(`collaterals.${index}.insured`, {
                            setValueAs: v => (v === true || v === 'true' ? true : v === false || v === 'false' ? false : undefined),
                          })}
                        >
                          <option value="">Not recorded yet</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-group">
                        <label htmlFor={`insurer-${index}`}>Insurer</label>
                        <input
                          type="text"
                          maxLength={100}
                          className="form-control"
                          id={`insurer-${index}`}
                          placeholder="e.g. Santam"
                          disabled={watchedCollaterals[index]?.insured !== true}
                          {...register(`collaterals.${index}.insurer`)}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-group">
                        <label htmlFor={`insurancePolicyNumber-${index}`}>Policy Number</label>
                        <input
                          type="text"
                          maxLength={60}
                          className="form-control"
                          id={`insurancePolicyNumber-${index}`}
                          disabled={watchedCollaterals[index]?.insured !== true}
                          {...register(`collaterals.${index}.insurancePolicyNumber`)}
                        />
                      </div>
                    </div>
                    {watchedCollaterals[index]?.insured !== true && (
                      <div className="col-12">
                        <div className="alert alert-warning small mb-0">
                          <i className="fa fa-shield me-2"></i>
                          The credit manual requires the vehicle to be insured for the facility. Without proof of insurance
                          it becomes a condition to meet before payout.
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`otherDetails-${index}`}>Other Details (Optional)</label>
                    <textarea
                      className={`form-control ${errors.collaterals?.[index]?.otherDetails ? 'is-invalid' : ''}`}
                      id={`otherDetails-${index}`}
                      rows={2}
                      placeholder="Enter any other relevant details about the collateral"
                      {...register(`collaterals.${index}.otherDetails`)}
                    ></textarea>
                    {errors.collaterals?.[index]?.otherDetails && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.otherDetails?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`ownerDirector-${index}`}>Who owns it?</label>
                    <select
                      className="form-select"
                      id={`ownerDirector-${index}`}
                      value={ownerOf(index)}
                      onChange={e => chooseOwner(index, e.target.value)}
                    >
                      <option value="">Choose the owner</option>
                      {directors.map((d, i) => (
                        <option key={i} value={`d${i}`}>A director: {`${d.firstName || ''} ${d.lastName || ''}`.trim()}</option>
                      ))}
                      <option value="company">The company{companyName ? `: ${companyName}` : ''}</option>
                      <option value="third">A third party</option>
                    </select>
                    <small className="form-text text-muted">
                      {ownerOf(index) === 'third'
                        ? "Enter the third party's full name and ID or registration number below."
                        : "Choosing a director or the company fills in the owner's name and ID or registration number below. You can still change them."}
                    </small>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label htmlFor={`ownerName-${index}`}>Owner's Full Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.collaterals?.[index]?.ownerName ? 'is-invalid' : ''}`}
                      id={`ownerName-${index}`}
                      placeholder="Enter owner's full name"
                      {...register(`collaterals.${index}.ownerName`, {
                        required: hasCollateral ? 'Owner name is required' : false
                      })}
                    />
                    {errors.collaterals?.[index]?.ownerName && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.ownerName?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label htmlFor={`ownerId-${index}`}>Owner's ID / Registration Number</label>
                    <input
                      type="text"
                      className={`form-control ${errors.collaterals?.[index]?.ownerId ? 'is-invalid' : ''}`}
                      id={`ownerId-${index}`}
                      maxLength={50}
                      placeholder="ID number, or company reg. no. e.g. 2014/258066/07"
                      // Validation lives in the wizard's zod schema — inline rules
                      // are ignored while a zodResolver is attached.
                      {...register(`collaterals.${index}.ownerId`)}
                    />
                    {errors.collaterals?.[index]?.ownerId && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.ownerId?.message as string}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`value-${index}`}>Value Lent Against (R)</label>
                    <input
                      type="number"
                      className={`form-control ${errors.collaterals?.[index]?.value ? 'is-invalid' : ''}`}
                      id={`value-${index}`}
                      placeholder="For a vehicle: the discounted value, filled in from the retail value"
                      {...register(`collaterals.${index}.value`, {
                        required: hasCollateral ? 'Estimated value is required' : false,
                        valueAsNumber: true,
                        min: hasCollateral ? {
                          value: 0,
                          message: 'Value must be greater than 0'
                        } : undefined
                      })}
                    />
                    {errors.collaterals?.[index]?.value && (
                      <div className="invalid-feedback">{errors?.collaterals?.[index]?.value?.message as string}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {alreadyOwed > 0 && (
            <div className="alert alert-light border" role="status">
              <i className="fa fa-info-circle me-2 text-primary"></i>
              This customer still owes <strong>{formatRand(alreadyOwed)}</strong> on {existing?.accounts} open
              loan{existing?.accounts === 1 ? '' : 's'}. With this loan, Tirhelo's exposure is
              {' '}<strong>{formatRand(exposureAfter)}</strong>, and the collateral is measured against that.
            </div>
          )}

          {loanAmount > 0 && lentAgainst > 0 && exposureAfter > lentAgainst && (
            <div className="alert alert-danger" role="alert">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {alreadyOwed > 0 ? (
                <>Tirhelo's exposure after this loan, <strong>{formatRand(exposureAfter)}</strong> (this loan
                {' '}{formatRand(loanAmount)} plus {formatRand(alreadyOwed)} already owed), is more than the collateral's
                value lent against (<strong>{formatRand(lentAgainst)}</strong>). The shortfall is
                {' '}<strong>{formatRand(exposureAfter - lentAgainst)}</strong>: lower the loan amount or add collateral.</>
              ) : (
                <>The loan of <strong>{formatRand(loanAmount)}</strong> is more than the collateral's value lent against
                (<strong>{formatRand(lentAgainst)}</strong>). Tirhelo lends up to the discounted value: lower the loan amount
                or add collateral.</>
              )}
            </div>
          )}

          <div className="d-flex justify-content-center mb-3">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addCollateral}
            >
              <i className="fa fa-plus me-1"></i>
              Add Another Collateral
            </button>
          </div>

          <div className="alert alert-warning">
            <div className="d-flex">
              <div className="me-3">
                <i className="fa fa-exclamation-triangle fa-lg"></i>
              </div>
              <div>
                <p className="mb-0">
                  <strong>Note:</strong> You may be required to provide documentation proving ownership
                  and value of the collateral during the verification process.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-info">
          <div className="d-flex">
            <div className="me-3">
              <i className="fa fa-info-circle fa-lg"></i>
            </div>
            <div>
              <p className="mb-0">
                You have chosen not to provide collateral for this loan application.
                This may affect your loan approval chances or interest rates.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
